import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { PublicWorkout } from 'hevy-shared';
import { Link } from 'components/Link';
import { Card } from 'styleguide/Card';
import { Screen } from 'styleguide/ScreenContainer';
import { TextSMMedium } from 'styleguide/Texts';
import { UserCard } from './components/UserCard';
import { UserHeader } from './components/UserHeader';
import dayjs from 'dayjs';
import { WorkoutDetailPublicViewModel } from './WorkoutDetailPublicViewModel';
import { Exercise } from './components/Exercise';
import { DividerLine } from 'styleguide/DividerLine';
import { observer } from 'mobx-react-lite';
import { PrivateWorkout } from './components/PrivateWorkout';
import { AppStoresCard } from './components/AppStoresCard';
import { Stats } from 'components/WorkoutCell/components/Stats';
import { sendEvent } from 'utils/events';
import DownloadGooglePlay from 'assets/download-googleplay.svg';
import { Spacing } from 'styleguide/spacing';
import { View } from 'styleguide/View';
import { exercisesToSupersetGroups } from 'components/ExerciseSummary/utils';
import { SupersetGroupCell } from 'components/ExerciseSummary/SupersetGroupCell';

const Content = styled.div`
  display: grid;
  max-width: 1024px;
  justify-content: center;
  grid-template-columns: 5fr 3fr;
  gap: 20px;
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const LeftContainer = styled(Card)`
  display: flex;
  flex-direction: column;
`;

const RightContainer = styled(View)`
  @media (max-width: 1024px) {
    display: none;
  }
`;

const TopContainer = styled(View)`
  gap: ${Spacing.md}px;
  padding: ${Spacing.lg}px;
  padding-bottom: ${Spacing.md}px;
`;

const BottomContainer = styled(View)`
  gap: ${Spacing.md}px;
  padding: ${Spacing.lg}px;
  padding-top: ${Spacing.md}px;
`;

const MobileAppStoreButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  @media (min-width: 1024px) {
    display: none;
  }
`;

interface WorkoutDetailPublicScreenProps {
  workout: PublicWorkout;
}

export const WorkoutDetailPublicScreen = ({ workout }: WorkoutDetailPublicScreenProps) => {
  const [vm, setVm] = useState(new WorkoutDetailPublicViewModel(workout));

  useEffect(() => {
    vm.refreshContent();
  }, []);

  useEffect(() => {
    if (vm.workout.id !== workout.id) {
      const newVm = new WorkoutDetailPublicViewModel(workout);
      setVm(newVm);
      newVm.refreshContent();
    }
  }, [workout.id]);

  useEffect(() => {
    setTimeout(() => {
      /**
       * Without setTimeout this event is fired before
       * amplitude has been initialized.
       */
      sendEvent('workoutDetail_screenview', {
        authenticated: false,
      });
    });
  }, []);

  return <WorkoutDetailPublicScreenInner vm={vm} />;
};

interface WorkoutDetailPublicScreenInnerProps {
  vm: WorkoutDetailPublicViewModel;
}

export const WorkoutDetailPublicScreenInner = observer(
  ({ vm }: WorkoutDetailPublicScreenInnerProps) => {
    const { workout, userProfile } = vm;

    const supersetGroups = exercisesToSupersetGroups(workout);
    return (
      <Screen>
        <Content>
          {/*
           * preload the SVG gradient from the Google Play logo. if there are multiple
           * SVGs with the same linearGradient `id` on the same page, hiding the first one
           * using `display: none` makes the gradient disappear from all of them.
           * something like https://github.com/elderapo/react-svg-unique-id/ would be a
           * better fix, but the library is outdated and doesn't work within our environment.
           */}
          <DownloadGooglePlay style={{ width: 0, height: 0, position: 'absolute' }} />
          <LeftContainer>
            {workout.is_private && <PrivateWorkout username={workout.username} />}
            {!workout.is_private && (
              <>
                <TopContainer>
                  <Link href={`/user/${workout.username}`}>
                    <UserHeader
                      username={workout.username}
                      date={dayjs.unix(workout.end_time).fromNow()}
                      profileImage={workout.profile_image ?? undefined}
                    />
                  </Link>

                  <Link
                    href={`/user/${workout.username}`}
                    style={{ display: 'flex', flexDirection: 'column', gap: Spacing.md }}
                  >
                    <TextSMMedium>{workout.name}</TextSMMedium>
                    <Stats stats={vm.stats} />
                  </Link>
                </TopContainer>

                <DividerLine />

                <BottomContainer>
                  {vm.showExerciseDetails ? (
                    <>
                      {vm.exercises.map(e => (
                        <Exercise
                          key={e.key}
                          image={e.image}
                          title={e.title}
                          supersetId={e.supersetId}
                          notes={e.notes}
                          secondColumnHeader={e.secondColumnHeader}
                          sets={e.sets}
                        />
                      ))}
                    </>
                  ) : (
                    <>
                      {supersetGroups.map((group, index) => (
                        <SupersetGroupCell
                          key={index}
                          supersetId={group.supersetId}
                          exercises={group.exercises}
                          showDivider={index !== supersetGroups.length - 1}
                        />
                      ))}
                      .
                    </>
                  )}
                </BottomContainer>
              </>
            )}
            <MobileAppStoreButtonsContainer>
              <AppStoresCard
                style={{ marginTop: 16 }}
                username={workout.username}
                onCopyPress={vm.onCopyLinkPress}
              />
            </MobileAppStoreButtonsContainer>
          </LeftContainer>
          <RightContainer>
            <UserCard
              username={workout.username}
              profileImage={workout.profile_image ?? undefined}
              workoutCount={userProfile ? `${userProfile.workout_count}` : '-'}
              followerCount={userProfile ? `${userProfile.follower_count}` : '-'}
              followingCount={userProfile ? `${userProfile.following_count}` : '-'}
              showFollowButton={false}
            />
            <AppStoresCard
              style={{ marginTop: 16 }}
              username={workout.username}
              onCopyPress={vm.onCopyLinkPress}
            />
          </RightContainer>
        </Content>
      </Screen>
    );
  },
);
